import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type ExportFormat,
  type ExportScope,
  type ExportTarget,
  scopeNarrows,
  scopeNarrowsColumns,
  useExportFile,
} from '@/entities/database';
import { FormError } from '@/shared/forms';
import { Button, Dialog, Icons } from '@/shared/ui';

import styles from './Portability.module.css';

interface Props {
  target: ExportTarget;
  name: string;
  confidential: boolean;
  /** Whether the viewer may take the records out (structure only is always possible here). */
  recordsAllowed: boolean;
  /** What the open table shows (only for that table), so "the current view" can be offered. */
  scope?: ExportScope | undefined;
  onClose: () => void;
}

/**
 * "Export to a file": the native Planosfera JSON snapshot (a backup, structure only or with
 * records), or a flat CSV / Excel file of the records (a table; Excel also a whole database, one
 * sheet per table). The browser downloads straight to disk; the file is outside Planosfera's access
 * control, which the dialog says plainly. Not the same as "Duplicate" (a copy inside the app).
 * The server filters and sorts "the current view" itself: nothing is assembled from the rows the
 * screen has loaded.
 */
export function ExportSnapshotDialog({
  target,
  name,
  confidential,
  recordsAllowed,
  scope,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const exportFile = useExportFile();
  const kind = target.kind;
  const [format, setFormat] = useState<ExportFormat>('json');
  const [records, setRecords] = useState(recordsAllowed);
  const [useView, setUseView] = useState(false);
  const [visibleOnly, setVisibleOnly] = useState(false);
  const flat = format !== 'json';
  const formats: ExportFormat[] = kind === 'table' ? ['json', 'csv', 'xlsx'] : ['json', 'xlsx'];
  const viewScope = kind === 'table' && flat && scope && scopeNarrows(scope) ? scope : undefined;
  const columnScope =
    kind === 'table' &&
    flat &&
    scope &&
    scopeNarrowsColumns(scope) &&
    scope.visibleColumns.length > 0
      ? scope
      : undefined;
  const flatBlocked = flat && !recordsAllowed;
  const describe = (value: ExportFormat): string =>
    value === 'csv'
      ? t('databases.portability.export.format.csv.table')
      : kind === 'table'
        ? t(`databases.portability.export.format.${value}.table`)
        : t(`databases.portability.export.format.${value}.database`);

  return (
    <Dialog title={t(`databases.portability.export.title.${kind}`, { name })} onClose={onClose}>
      <form
        className={styles.form}
        data-testid="export-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (exportFile.isPending || flatBlocked) return;
          exportFile.mutate(
            {
              target,
              format,
              records: flat || records,
              ...(viewScope && useView && { scope: viewScope }),
              ...(columnScope && visibleOnly && { visibleColumnsOnly: true }),
              ...(!(viewScope && useView) && columnScope && visibleOnly && { scope: columnScope }),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <p className={styles.intro}>{t('databases.portability.export.intro')}</p>
        <fieldset className={styles.modes}>
          <legend className={styles.legend}>
            {t('databases.portability.export.formatLegend')}
          </legend>
          {formats.map((value) => (
            <label key={value} className={styles.mode}>
              <input
                type="radio"
                name="export-format"
                value={value}
                checked={format === value}
                onChange={() => {
                  setFormat(value);
                }}
              />
              <span>
                <span className={styles.modeTitle}>
                  {t(`databases.portability.export.format.${value}.title`)}
                </span>
                <span className={styles.modeDesc}>{describe(value)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {!flat && (
          <fieldset className={styles.modes}>
            <legend className="visually-hidden">{t('databases.duplicate.mode')}</legend>
            <label className={styles.mode}>
              <input
                type="radio"
                name="export-mode"
                checked={!records}
                onChange={() => {
                  setRecords(false);
                }}
              />
              <span>
                <span className={styles.modeTitle}>{t('databases.duplicate.structureOnly')}</span>
                <span className={styles.modeDesc}>
                  {t('databases.portability.export.structureHint')}
                </span>
              </span>
            </label>
            <label className={styles.mode} data-disabled={!recordsAllowed}>
              <input
                type="radio"
                name="export-mode"
                checked={records}
                disabled={!recordsAllowed}
                onChange={() => {
                  setRecords(true);
                }}
              />
              <span>
                <span className={styles.modeTitle}>{t('databases.duplicate.withRecords')}</span>
                <span className={styles.modeDesc}>
                  {recordsAllowed
                    ? t('databases.portability.export.recordsHint')
                    : t('databases.portability.export.recordsDenied')}
                </span>
              </span>
            </label>
          </fieldset>
        )}

        {flat && kind === 'table' && (
          <fieldset className={styles.modes}>
            <legend className={styles.legend}>
              {t('databases.portability.export.scopeLegend')}
            </legend>
            <label className={styles.mode}>
              <input
                type="radio"
                name="export-scope"
                checked={!(viewScope && useView)}
                onChange={() => {
                  setUseView(false);
                }}
              />
              <span>
                <span className={styles.modeTitle}>
                  {t('databases.portability.export.scopeAll')}
                </span>
                <span className={styles.modeDesc}>
                  {t('databases.portability.export.scopeAllHint')}
                </span>
              </span>
            </label>
            {viewScope && (
              <label className={styles.mode}>
                <input
                  type="radio"
                  name="export-scope"
                  checked={useView}
                  onChange={() => {
                    setUseView(true);
                  }}
                />
                <span>
                  <span className={styles.modeTitle}>
                    {t('databases.portability.export.scopeView')}
                  </span>
                  <span className={styles.modeDesc}>
                    {t('databases.portability.export.scopeViewHint', {
                      parts: [
                        viewScope.filters.length > 0 &&
                          t('databases.portability.export.scopeFilters', {
                            count: viewScope.filters.length,
                          }),
                        viewScope.q.trim() !== '' && t('databases.portability.export.scopeSearch'),
                        viewScope.sort.length > 0 &&
                          t('databases.portability.export.scopeSort', {
                            count: viewScope.sort.length,
                          }),
                      ]
                        .filter(Boolean)
                        .join(', '),
                    })}
                  </span>
                </span>
              </label>
            )}
          </fieldset>
        )}
        {columnScope && (
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={visibleOnly}
              onChange={(event) => {
                setVisibleOnly(event.target.checked);
              }}
            />
            <span>
              <span className={styles.modeTitle}>
                {t('databases.portability.export.visibleOnly', {
                  shown: columnScope.visibleColumns.length,
                  total: columnScope.allColumns.length,
                })}
              </span>
              <span className={styles.modeDesc}>
                {t('databases.portability.export.visibleOnlyHint')}
              </span>
            </span>
          </label>
        )}

        {flat && (
          <p className={styles.note} role="note" data-testid="flat-note">
            {t(`databases.portability.export.flatNote.${format === 'csv' ? 'csv' : 'xlsx'}`)}
          </p>
        )}
        {flatBlocked && (
          <p className={styles.warning} role="note">
            {t('databases.portability.export.recordsDenied')}
          </p>
        )}
        <p className={styles.notice} role="note">
          <Icons.lock /> {t('databases.portability.export.fileNote')}
          {confidential && ` ${t('databases.portability.export.confidentialNote')}`}
        </p>
        {(flat || records) && (
          <p className={styles.warning} role="note">
            {t('databases.duplicate.personalData')}
          </p>
        )}
        <FormError error={exportFile.error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={exportFile.isPending || flatBlocked}>
            <Icons.export />{' '}
            {exportFile.isPending
              ? t('databases.portability.export.preparing')
              : t(`databases.portability.export.download_${format}`)}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
